/**
 * Withdraw — вывод средств с рублёвого счёта (Flow step="withdraw"). Два внутренних шага:
 * 1) выбор способа (зарубежная карта / SWIFT / крипто-кошелёк); 2) реквизиты + сумма.
 * Сумма вводится и в рублях, и в USD (двусторонний пересчёт по текущему курсу). С баланса
 * списываются рубли (холд), курс фиксируется на сервере в момент запроса. Крипто: сеть TRC20,
 * комиссия из настроек (по умолчанию 3 USD), клиент получает (брутто − комиссия) USDT.
 * Карта/SWIFT — без комиссии. Пропсы: balance (₽), onNext(withdrawal), onBack.
 *
 * source="card" — тот же экран для вывода с виртуальной карты: её баланс уже в долларах,
 * поэтому рублёвого поля и курса нет, сумма вводится в USD и списывается с карты. Способ
 * приходит готовым (initialMethod) из CardWithdraw, крипта туда не попадает — она отдельная
 * операция карты (конвертация с выводом одним действием).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useBackButton, useMainButton } from "@/telegram/useMainButton";
import { FallbackButton, Screen } from "@/components/Screen";
import { Hero } from "@/components/Hero";
import { Field } from "@/components/Field";
import { MaxButton } from "@/components/MaxButton";
import { Note } from "@/components/Note";
import { GlowIcon } from "@/components/GlowIcon";
import {
  ApiError,
  friendlyError,
  rateApi,
  withdrawalsApi,
  type ApiWithdrawal,
  type CreateWithdrawalIn,
  type WithdrawalMethod,
} from "@/lib/api";
import { formatRub, validateTrc20 } from "@/lib/format";

type Props = {
  balance: number; // рублёвый баланс счёта (для source="card" не используется)
  onNext: (w: ApiWithdrawal) => void;
  onBack: () => void;
  onOpenSof: () => void; // переход на форму ИПДС (из окна превышения лимита)
  source?: "account" | "card";
  balanceUsd?: number;              // баланс карты, когда выводим с неё
  initialMethod?: WithdrawalMethod; // способ выбран заранее (вывод с карты)
};

const METHODS: { key: WithdrawalMethod; icon: string; title: string; subtitle: string }[] = [
  { key: "card", icon: "💳", title: "Зарубежная карта", subtitle: "Перевод на карту иностранного банка" },
  { key: "swift", icon: "🏦", title: "SWIFT-перевод", subtitle: "Международный банковский перевод" },
  { key: "crypto", icon: "🪙", title: "Крипто-кошелёк", subtitle: "USDT в сети TRC20" },
];

const LATIN = /^[A-Za-z][A-Za-z '-]*$/;
const CARD_NUM = /^\d{12,19}$/;
const SWIFT = /^[A-Za-z0-9]{8}([A-Za-z0-9]{3})?$/;
const IBAN = /^[A-Za-z0-9]{8,34}$/;

export function Withdraw({
  balance,
  onNext,
  onBack,
  onOpenSof,
  source = "account",
  balanceUsd = 0,
  initialMethod,
}: Props) {
  const fromCard = source === "card";
  const [method, setMethod] = useState<WithdrawalMethod | null>(initialMethod ?? null);
  const [cryptoFee, setCryptoFee] = useState(3);
  const [cryptoUnlocked, setCryptoUnlocked] = useState(false); // TRC20-кошелёк — только с читом hesoyam
  const [limitBlocked, setLimitBlocked] = useState(false); // вывод упёрся в лимит → предлагаем ИПДС
  const [rate, setRate] = useState<number | null>(null);
  const [rub, setRub] = useState("");
  const [usd, setUsd] = useState("");
  const [last, setLast] = useState<"rub" | "usd">("rub");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // поля реквизитов (плоско, используются по способу)
  const [cardNumber, setCardNumber] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [benef, setBenef] = useState("");
  const [iban, setIban] = useState("");
  const [swift, setSwift] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankCountry, setBankCountry] = useState("");
  const [purpose, setPurpose] = useState(""); // SWIFT: назначение платежа (необязательно)
  const [invoice, setInvoice] = useState<File | null>(null); // SWIFT: инвойс (необязательно)
  const [wallet, setWallet] = useState("");

  useEffect(() => {
    withdrawalsApi
      .config()
      .then((c) => {
        setCryptoFee(c.crypto_fee_usdt);
        setCryptoUnlocked(c.withdraw_enabled);
      })
      .catch(() => undefined);
    let alive = true;
    const loadRate = () => rateApi.current().then((r) => { if (alive) setRate(r.rate); }).catch(() => undefined);
    loadRate();
    const t = setInterval(loadRate, 15000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // при обновлении курса пересчитываем зависимое поле от последнего введённого
  // (при выводе с карты пересчёта нет — её баланс уже в долларах)
  useEffect(() => {
    if (!rate || fromCard) return;
    if (last === "rub") setUsd(rub ? (Number(rub) / rate).toFixed(2) : "");
    else setRub(usd && Number(usd) > 0 ? String(Math.round(Number(usd) * rate)) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rate]);

  const onRub = (raw: string) => {
    const d = raw.replace(/\D/g, "");
    setRub(d);
    setLast("rub");
    setUsd(rate && d ? (Number(d) / rate).toFixed(2) : "");
  };
  const onUsd = (raw: string) => {
    const c = raw.replace(/[^\d.,]/g, "").replace(",", ".").replace(/(\.\d*)\./g, "$1");
    setUsd(c);
    setLast("usd");
    const n = Number(c);
    setRub(rate && n > 0 ? String(Math.round(n * rate)) : "");
  };

  const rubNum = Number(rub);
  const usdGross = Number(usd);
  const fee = method === "crypto" ? cryptoFee : 0;
  const net = usdGross > 0 ? Math.max(0, usdGross - fee) : 0;
  // с карты списываем доллары напрямую: рублёвого поля и курса в этом режиме нет
  const availableUsd = fromCard ? balanceUsd : 0;

  const detailsValid = useMemo(() => {
    if (method === "card") return CARD_NUM.test(cardNumber.replace(/\s/g, "")) && LATIN.test(firstName.trim()) && LATIN.test(lastName.trim());
    if (method === "swift")
      return (
        LATIN.test(benef.trim()) &&
        IBAN.test(iban.replace(/\s/g, "")) &&
        SWIFT.test(swift.replace(/\s/g, "")) &&
        bankName.trim().length > 0 &&
        bankCountry.trim().length > 0
      );
    if (method === "crypto") return validateTrc20(wallet);
    return false;
  }, [method, cardNumber, firstName, lastName, benef, iban, swift, bankName, bankCountry, wallet]);

  const amountValid = fromCard
    ? usdGross > fee && usdGross <= availableUsd
    : rate !== null && rubNum > 0 && rubNum <= balance && usdGross > fee;
  const valid = !!method && amountValid && detailsValid;

  const amountErr = fromCard
    ? usd && usdGross > availableUsd
      ? `Не больше баланса карты — $${availableUsd.toFixed(2)}`
      : usd && usdGross > 0 && usdGross <= fee
      ? `Сумма должна покрывать комиссию (${fee} USD)`
      : undefined
    : rub && rubNum > balance
      ? `Не больше баланса — ${formatRub(balance)}`
      : rub && usdGross > 0 && usdGross <= fee
      ? `Сумма должна покрывать комиссию (${fee} USD)`
      : undefined;

  // инлайн-ошибки реквизитов: показываем, только когда поле непустое и невалидное
  const cardNumErr = cardNumber && !CARD_NUM.test(cardNumber.replace(/\s/g, "")) ? "Номер карты — 12–19 цифр" : undefined;
  const firstErr = firstName && !LATIN.test(firstName.trim()) ? "Только латиница" : undefined;
  const lastErr = lastName && !LATIN.test(lastName.trim()) ? "Только латиница" : undefined;
  const benefErr = benef && !LATIN.test(benef.trim()) ? "Только латиница" : undefined;
  const ibanErr = iban && !IBAN.test(iban.replace(/\s/g, "")) ? "Неверный IBAN / номер счёта" : undefined;
  const swiftErr = swift && !SWIFT.test(swift.replace(/\s/g, "")) ? "Неверный SWIFT / BIC" : undefined;
  const walletErr = wallet && !validateTrc20(wallet) ? "Неверный адрес TRC20 (T и 33 символа)" : undefined;

  const submit = useCallback(async () => {
    if (!valid || !method || submitting) return;
    setSubmitting(true);
    setError(null);
    setLimitBlocked(false);
    const body: CreateWithdrawalIn = fromCard
      ? { method, amount_rub: 0, source: "card", amount_usd: usdGross }
      : { method, amount_rub: rubNum };
    if (method === "card") body.card = { card_number: cardNumber.replace(/\s/g, ""), first_name: firstName.trim(), last_name: lastName.trim() };
    else if (method === "swift")
      body.swift = {
        beneficiary_name: benef.trim(),
        iban: iban.replace(/\s/g, ""),
        swift: swift.replace(/\s/g, ""),
        bank_name: bankName.trim(),
        bank_country: bankCountry.trim(),
        ...(purpose.trim() ? { purpose: purpose.trim() } : {}),
      };
    else body.crypto = { wallet_address: wallet.trim() };
    try {
      const created = await withdrawalsApi.create(body);
      // инвойс необязателен — прикрепляем после создания; сбой загрузки заявку не отменяет
      if (method === "swift" && invoice) {
        try {
          await withdrawalsApi.uploadInvoice(created.id, invoice);
        } catch {
          /* заявка создана, файл не приложился — не блокируем клиента */
        }
      }
      onNext(created);
    } catch (e) {
      if (e instanceof ApiError && e.detail === "limit_exceeded") {
        setLimitBlocked(true);
      } else if (e instanceof ApiError && e.detail === "kyc_pending") {
        setError("Документы на проверке. Вывод станет доступен после подтверждения верификации.");
      } else if (e instanceof ApiError && e.detail === "kyc_required") {
        setError("Для вывода нужна пройденная верификация личности (KYC). Пройдите её в разделе «Верификация» в профиле.");
      } else if (e instanceof ApiError && e.detail === "crypto_locked") {
        setError("Вывод на криптокошелёк сейчас недоступен.");
      } else {
        setError(friendlyError(e, "Не удалось создать заявку на вывод"));
      }
    } finally {
      setSubmitting(false);
    }
  }, [valid, method, submitting, fromCard, rubNum, usdGross, cardNumber, firstName, lastName, benef, iban, swift, bankName, bankCountry, purpose, invoice, wallet, onNext]);

  // назад: с формы — к выбору способа; из выбора способа — в кабинет. При выводе с карты
  // способ выбран заранее (на экране карты), поэтому возвращаемся сразу туда.
  const back = useCallback(() => {
    if (method && !initialMethod) {
      setMethod(null);
      setError(null);
    } else onBack();
  }, [method, initialMethod, onBack]);

  useBackButton(back);
  useMainButton({
    text: submitting ? "Создаём заявку..." : "Вывести",
    onClick: submit,
    visible: !!method,
    active: valid,
    loading: submitting,
  });

  // ── Шаг 1: выбор способа ──────────────────────────────────────────────
  if (!method) {
    return (
      <Screen>
        <Hero icon="🏧" title="Вывод средств" subtitle="Выберите, куда вывести деньги со счёта" />
        <div className="mb-4 rounded-2xl border border-white/[0.06] bg-surface-card px-4 py-3 text-center">
          <div className="text-[12px] text-white/45">Доступно к выводу</div>
          <div className="mt-0.5 text-[22px] font-semibold">{formatRub(balance)}</div>
        </div>
        <div className="space-y-2.5">
          {(cryptoUnlocked ? METHODS : METHODS.filter((m) => m.key !== "crypto")).map((m) => (
            <button
              key={m.key}
              onClick={() => setMethod(m.key)}
              className="flex w-full items-center gap-3 rounded-2xl border border-white/[0.06] bg-surface-card px-4 py-4 text-left active:bg-white/[0.03]"
            >
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand/15 text-[20px]">{m.icon}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-medium">{m.title}</span>
                <span className="block text-[13px] text-white/45">
                  {m.key === "crypto" ? `${m.subtitle} · комиссия ${cryptoFee} USD` : m.subtitle}
                </span>
              </span>
              <span className="shrink-0 text-brand">›</span>
            </button>
          ))}
        </div>
      </Screen>
    );
  }

  // ── Шаг 2: форма реквизитов + сумма ───────────────────────────────────
  const heroMeta = METHODS.find((m) => m.key === method)!;
  const rubDisplay = rub ? Number(rub).toLocaleString("ru-RU") : "";

  return (
    <Screen>
      <div className="mb-6 flex flex-col items-center text-center">
        <GlowIcon className="mb-4 h-16 w-16 rounded-[20px] text-[28px]">{heroMeta.icon}</GlowIcon>
        <h1 className="text-[22px] font-semibold leading-tight">{heroMeta.title}</h1>
        <button
          onClick={() => (initialMethod ? onBack() : setMethod(null))}
          className="mt-1 text-[13px] text-brand active:opacity-70"
        >
          Выбрать другой способ
        </button>
      </div>

      <div className="space-y-3">
        {!fromCard && (
          <Field
            label="Сумма со счёта"
            type="text"
            inputMode="numeric"
            autoFocus
            placeholder="0"
            value={rubDisplay}
            onChange={(e) => onRub(e.target.value)}
            suffix={
              <span className="flex items-center gap-2.5">
                <span className="text-[14px] text-white/45">₽</span>
                <MaxButton onClick={() => onRub(String(Math.floor(balance)))} disabled={balance <= 0} />
              </span>
            }
            error={amountErr}
            hint={!amountErr ? `Доступно: ${formatRub(balance)}` : undefined}
          />
        )}
        <Field
          label={fromCard ? "Сумма с карты" : "Сумма в USD"}
          type="text"
          inputMode="decimal"
          autoFocus={fromCard}
          placeholder="0"
          value={usd}
          onChange={(e) => onUsd(e.target.value)}
          suffix={
            <span className="flex items-center gap-2.5">
              <span className="text-[14px] text-white/45">USD</span>
              {/* с карты остаток известен в долларах; со счёта максимум задаётся рублёвым полем */}
              {fromCard && (
                <MaxButton
                  onClick={() => onUsd(String(Math.floor(availableUsd * 100) / 100))}
                  disabled={availableUsd <= 0}
                />
              )}
            </span>
          }
          error={fromCard ? amountErr : undefined}
          hint={fromCard && !amountErr ? `Доступно на карте: $${availableUsd.toFixed(2)}` : undefined}
        />

        {method === "card" && (
          <>
            <Field label="Номер карты" type="text" inputMode="numeric" placeholder="0000 0000 0000 0000" value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} error={cardNumErr} />
            <Field label="Имя (латиницей)" type="text" placeholder="IVAN" value={firstName} onChange={(e) => setFirstName(e.target.value)} error={firstErr} />
            <Field label="Фамилия (латиницей)" type="text" placeholder="IVANOV" value={lastName} onChange={(e) => setLastName(e.target.value)} error={lastErr} />
          </>
        )}

        {method === "swift" && (
          <>
            <Field label="Получатель (латиницей)" type="text" placeholder="IVAN IVANOV" value={benef} onChange={(e) => setBenef(e.target.value)} error={benefErr} />
            <Field label="IBAN / номер счёта" type="text" placeholder="DE00 0000 0000 0000 0000 00" value={iban} onChange={(e) => setIban(e.target.value)} error={ibanErr} />
            <Field label="SWIFT / BIC" type="text" placeholder="DEUTDEFF" value={swift} onChange={(e) => setSwift(e.target.value)} error={swiftErr} />
            <Field label="Название банка" type="text" placeholder="Deutsche Bank" value={bankName} onChange={(e) => setBankName(e.target.value)} />
            <Field label="Страна банка" type="text" placeholder="Germany" value={bankCountry} onChange={(e) => setBankCountry(e.target.value)} />
            <Field
              label="Назначение платежа (необязательно)"
              type="text"
              placeholder="e.g. Payment for invoice #123"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
            />
            <div>
              <div className="mb-1.5 text-[13px] text-white/45">Инвойс (необязательно)</div>
              <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-dashed border-white/15 bg-surface-card px-4 py-3.5 active:bg-white/[0.03]">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand/15 text-[16px]">📎</span>
                <span className="min-w-0 flex-1 truncate text-[14px] text-white/80">
                  {invoice ? invoice.name : "Прикрепить файл"}
                </span>
                {invoice && (
                  <span
                    role="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setInvoice(null);
                    }}
                    className="shrink-0 text-[13px] text-brand active:opacity-70"
                  >
                    Убрать
                  </span>
                )}
                <input
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={(e) => setInvoice(e.target.files?.[0] ?? null)}
                />
              </label>
            </div>
          </>
        )}

        {method === "crypto" && (
          <Field
            label="Кошелёк TRC20"
            type="text"
            placeholder="T..."
            value={wallet}
            onChange={(e) => setWallet(e.target.value)}
            error={walletErr}
            hint={!walletErr ? "Адрес сети TRON (TRC20)" : undefined}
          />
        )}
      </div>

      {fromCard ? (
        <Note className="mt-4">
          Списывается с карты, без комиссии и пересчёта. К получению:{" "}
          <span className="font-medium text-white">{net.toFixed(2)} USD</span>.
        </Note>
      ) : method === "crypto" ? (
        <Note className="mt-4">
          Курс: {rate ? `${rate.toFixed(2)} ₽/USD` : "—"} · комиссия {fee} USD (сеть TRC20). К получению:{" "}
          <span className="font-medium text-white">{net.toFixed(2)} USDT</span>.
        </Note>
      ) : (
        <Note className="mt-4">
          Курс: {rate ? `${rate.toFixed(2)} ₽/USD` : "—"} · без комиссии. К получению:{" "}
          <span className="font-medium text-white">{net.toFixed(2)} USD</span>.
        </Note>
      )}

      {limitBlocked && (
        <Note variant="danger" className="mt-4">
          <div className="text-[15px] font-medium">Лимит на вывод превышен</div>
          <div className="mt-1 text-[13px] leading-snug">
            Запрошенная сумма превышает доступный лимит вывода. Чтобы увеличить лимит, пройдите
            верификацию источника происхождения денежных средств (ИПДС).
          </div>
          <button
            onClick={onOpenSof}
            className="mt-3 w-full rounded-2xl bg-brand px-4 py-3 text-[15px] font-semibold text-surface-base active:brightness-95"
          >
            Пройти ИПДС
          </button>
        </Note>
      )}

      {error && !limitBlocked && (
        <Note variant="danger" className="mt-4">
          {error}
        </Note>
      )}

      <FallbackButton text={submitting ? "..." : "Вывести"} onClick={submit} disabled={!valid} loading={submitting} />
    </Screen>
  );
}
